import React from 'react';
import { Upload, FileSpreadsheet, FileText, X } from 'lucide-react';

const FileUploadModal = ({
  uploadType,
  setUploadType,
  databaseName,
  setDatabaseName,
  selectedFile,
  setSelectedFile,
  fileInputRef,
  procedureFileInputRef,
  handleFileSelect,
  handleUploadSelectedFile,
  fileUploading,
  setShowUploadModal
}) => {
  // Function to get file size in readable format
  const getFileSize = (size) => {
    if (size < 1024) return `${size} bytes`;
    else if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
    else return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-xl font-semibold text-primary mb-4">Upload File</h3>
        
        {/* Upload Type Selection */}
        <div className="mb-4">
          <div className="flex space-x-4">
            <button
              className={`flex-1 py-2 px-4 rounded-md flex items-center justify-center gap-2 ${
                uploadType === 'metadata' 
                  ? 'bg-primary text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => {
                setUploadType('metadata');
                setSelectedFile(null);
              }}
            >
              <FileSpreadsheet className="w-5 h-5" />
              CSV Metadata
            </button>
            <button
              className={`flex-1 py-2 px-4 rounded-md flex items-center justify-center gap-2 ${
                uploadType === 'procedure' 
                  ? 'bg-primary text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => {
                setUploadType('procedure');
                setSelectedFile(null);
              }}
            >
              <FileText className="w-5 h-5" />
              Procedure File
            </button>
          </div>
        </div>
        
        {/* Database Name Input (for procedure files) */}
        {uploadType === 'procedure' && (
          <div className="mb-4">
            <label htmlFor="database-name" className="block text-sm font-medium text-gray-700 mb-1">
              Database Name
            </label>
            <input
              type="text"
              id="database-name"
              value={databaseName}
              onChange={(e) => setDatabaseName(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              placeholder="Enter database name"
              autoFocus={uploadType === 'procedure'}
            />
          </div>
        )}
        
        {/* Selected File Display */}
        {selectedFile ? (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {uploadType === 'metadata' ? (
                  <FileSpreadsheet className="w-6 h-6 text-primary mr-2" />
                ) : (
                  <FileText className="w-6 h-6 text-primary mr-2" />
                )}
                <div>
                  <p className="font-medium text-gray-800">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500">
                    {getFileSize(selectedFile.size)} • {selectedFile.type || 'Unknown type'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedFile(null)}
                className="p-1 hover:bg-gray-200 rounded-full"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
        ) : (
          /* File Upload Area */
          <div 
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 mb-4 text-center cursor-pointer hover:bg-gray-50"
            onClick={() => {
              if (uploadType === 'metadata') {
                fileInputRef.current?.click();
              } else {
                procedureFileInputRef.current?.click();
              }
            }}
          >
            <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-700">
              {uploadType === 'metadata' 
                ? 'Click to select CSV file' 
                : 'Click to select PDF/TXT file'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {uploadType === 'metadata' 
                ? 'CSV files only' 
                : 'PDF or TXT files accepted'}
            </p>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => handleFileSelect(e, 'metadata')}
              className="hidden"
              accept=".csv"
              disabled={fileUploading}
            />
            
            <input
              type="file"
              ref={procedureFileInputRef}
              onChange={(e) => handleFileSelect(e, 'procedure')}
              className="hidden"
              accept=".pdf,.txt"
              disabled={fileUploading}
            />
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setShowUploadModal(false)}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={fileUploading}
          >
            Cancel
          </button>
          {!selectedFile ? (
            <button
              onClick={() => {
                if (uploadType === 'metadata') {
                  fileInputRef.current?.click();
                } else {
                  procedureFileInputRef.current?.click();
                }
              }}
              className="px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondaryDark"
              disabled={fileUploading}
            >
              Select File
            </button>
          ) : (
            <button
              onClick={handleUploadSelectedFile}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primaryDark"
              disabled={fileUploading || (uploadType === 'procedure' && !databaseName.trim())}
            >
              {fileUploading ? 'Uploading...' : 'Upload'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileUploadModal;